import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { Navigation, RefreshCw, ArrowUpRight, TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, ChevronLeft } from "lucide-react";

function DriftGauge({ score }) {
  if (score === null || score === undefined) return null;
  const angle = -135 + (score / 100) * 270;
  const aligned = score < 30;
  const moderate = score < 60;
  const color = aligned ? "#00C781" : moderate ? "#F59E0B" : "#EF4444";
  const label = aligned ? "Aligned" : moderate ? "Drifting" : "Off course";
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width="140" height="80" viewBox="-70 -70 140 30">
        <path d="M -55 0 A 55 55 0 0 1 55 0" fill="none" stroke="#1A1A1A" strokeWidth="8" strokeLinecap="round"/>
        <path d="M -55 0 A 55 55 0 0 1 55 0" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${(score/100)*172.8} 172.8`}/>
        <g transform={`rotate(${angle})`}>
          <line x1="0" y1="0" x2="0" y2="-44" stroke={color} strokeWidth="2" strokeLinecap="round"/>
          <circle cx="0" cy="0" r="4" fill={color}/>
        </g>
        <text x="0" y="20" textAnchor="middle" fill={color} fontSize="18" fontWeight="700">{score}</text>
      </svg>
      <span className="text-[11px] font-semibold" style={{ color }}>{label}</span>
    </div>
  );
}

export default function Drift() {
  const { current } = useWorkspace();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try { const { data: d } = await api.get(`/workspaces/${wsId}/drift`); setData(d); }
    catch {} finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { load(); }, [load]);

  const SigIcon = (sev) => sev === "positive" ? TrendingUp : sev === "warning" ? AlertTriangle : Minus;
  const sigColor = (sev) => sev === "positive" ? "#00C781" : sev === "warning" ? "#F59E0B" : "#52525B";

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-1.5 -ml-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#71717A] hover:text-[#A1A1AA] transition-colors flex-shrink-0" title="Back to Overview">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-xl bg-[#14B8A6]/10 flex items-center justify-center"><Navigation className="w-4 h-4 text-[#14B8A6]"/></div>
          <div>
            <h1 className="text-[18px] font-bold text-[#EDEDED]">Drift</h1>
            <p className="text-[11px] text-[#3A3A3A]">How aligned is your activity with your Compass?</p>
          </div>
        </div>
        <button onClick={load} className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA]">
          <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`}/>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-6 max-w-2xl">
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 text-[#3A3A3A] animate-spin"/></div>
        ) : !data ? (
          <div className="text-center py-12 text-[#3A3A3A]">Failed to load drift data</div>
        ) : data.drift_score === null ? (
          <div className="flex flex-col items-center gap-4 py-12">
            <Navigation className="w-10 h-10 text-[#3A3A3A]"/>
            <p className="text-[14px] font-semibold text-[#52525B]">No Compass set yet</p>
            <p className="text-[12px] text-[#3A3A3A]">{data.message}</p>
            <button onClick={() => navigate("/compass")} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#5A90F5] text-white text-[12px] font-semibold hover:bg-[#4A80E5]">
              Set up Compass <ArrowUpRight className="w-3.5 h-3.5"/>
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Score */}
            <div className="flex items-start gap-8 p-6 bg-[#111] border border-[#2A2A2A] rounded-2xl">
              <DriftGauge score={data.drift_score}/>
              <div className="flex-1 pt-2">
                <p className="text-[13px] text-[#EDEDED] font-semibold mb-1">30-day alignment score</p>
                <p className="text-[12px] text-[#52525B] mb-4">Based on {data.total_activity_30d} activity signals across your workspace.</p>
                <p className="text-[11px] text-[#3A3A3A]">Lower is better — 0 means perfectly aligned, 100 means completely off course.</p>
              </div>
            </div>

            {/* Signals */}
            {data.signals?.length > 0 && (
              <div>
                <h3 className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider mb-3">Signals</h3>
                <div className="space-y-2">
                  {data.signals.map((s, i) => {
                    const Ic = SigIcon(s.severity);
                    return (
                      <div key={i} className="flex items-center gap-3 p-3 bg-[#111] border border-[#2A2A2A] rounded-xl">
                        <Ic className="w-3.5 h-3.5 flex-shrink-0" style={{color:sigColor(s.severity)}}/>
                        <span className="text-[12px] text-[#A1A1AA]">{s.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Goals */}
            {data.goals?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[11px] font-semibold text-[#52525B] uppercase tracking-wider">Compass Goals</h3>
                  <button onClick={()=>navigate("/compass")} className="text-[10px] text-[#5A90F5] hover:underline flex items-center gap-1">Edit <ArrowUpRight className="w-3 h-3"/></button>
                </div>
                <div className="space-y-2">
                  {data.goals.map((g, i) => (
                    <div key={i} className="p-3 bg-[#111] border border-[#2A2A2A] rounded-xl">
                      <p className="text-[12px] font-medium text-[#EDEDED]">{g.title}</p>
                      {g.timeframe && <p className="text-[10px] text-[#52525B] mt-0.5">{g.timeframe}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={()=>navigate("/compass")} className="flex-1 py-2.5 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] hover:border-[#3A3A3A] flex items-center justify-center gap-2">
                <Navigation className="w-3.5 h-3.5"/> Update Compass
              </button>
              <button onClick={()=>navigate("/review")} className="flex-1 py-2.5 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA] hover:border-[#3A3A3A] flex items-center justify-center gap-2">
                Start Review
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
